// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import getRealTimeWarehouseInventory from "@insite/client-framework/Store/Data/RealTimeInventory/Handlers/GetRealTimeWarehouseInventory";
import { xGoogleAnalyticsState } from "@sc/App/GoogleAnalyticsConnector/Store/Reducers";
import { getDataLayer } from "@sc/App/GoogleAnalyticsConnector/Utilities/DataLayer";
import { parseProductCategories, parseBoolean, safeJsonParse } from "@sc/XDL/Utilities";
import translate from "@insite/client-framework/Translate";
import {
    AvailabilityDto,
    AvailabilityMessageType,
    ProductModel,
    ProductSettingsModel,
    WarehouseDto,
} from "@insite/client-framework/Types/ApiModels";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell from "@insite/mobius/DataTable/DataTableCell";
import { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconWrapper } from "@insite/mobius/Icon";
import AlertTriangle from "@insite/mobius/Icons/AlertTriangle";
import Check from "@insite/mobius/Icons/Check";
import X from "@insite/mobius/Icons/X";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import resolveColor from "@insite/mobius/utilities/resolveColor";
import safeColor from "@insite/mobius/utilities/safeColor";
import Notify from "@sc/App/Components/Notify";
import DataTable, { DataTableProps } from "@sc/MobiusOverrides/DataTable/DataTable";
import AvailableForOrder from "@sc/XDL/Icons/AvailableForOrder";
import InStock from "@sc/XDL/Icons/InStock";
import OutOfStock from "@sc/XDL/Icons/OutOfStock";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

interface OwnProps {
    productId: string;
    availability?: AvailabilityDto;
    unitOfMeasure: string;
    trackInventory: boolean;
    isProductDetailsPage?: boolean;
    failedToLoadInventory?: boolean;
    extendedStyles?: ProductAvailabilityStyles;
    // XNG-CHANGE
    showOutOfStockText?: boolean;
    productInfo?: ProductInfo;
    product?: ProductModel;
    // These three values should be passed in cart and saved order details page
    isCartPage?: boolean;
    totalAvailableQty?: number;
    inBranchQty?: number;
    canBackOrder?: boolean;
    customAvailabilityMessage?: string;
}

const mapStateToProps = (state: ApplicationState) => ({
    productSettings: getSettingsCollection(state).productSettings,
    showInventory: state.context.showInventory,
    // XNG-Change
    fulfillmentMethod: getSession(state).fulfillmentMethod,
    hideWebInventory: parseBoolean(getSession(state)?.properties?.hideWebInventory?.toLowerCase()),
    xGoogleAnalytics: (state as xGoogleAnalyticsState).xGoogleAnalytics,
    session: getSession(state),
});

const mapDispatchToProps = {
    getRealTimeWarehouseInventory,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ProductAvailabilityStyles {
    container?: GridContainerProps;
    messageGridItem?: GridItemProps;
    availabilityByWarehouseLinkGridItem?: GridItemProps;
    inventoryMessage?: InjectableCss;
    messageText?: TypographyPresentationProps;
    realTimeText?: TypographyPresentationProps;
    errorWrapper?: InjectableCss;
    errorText?: TypographyPresentationProps;
    availabilityByWarehouseLink?: LinkPresentationProps;
    availabilityByWarehouseModal?: ModalPresentationProps;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    availabilityByWarehouseTable?: DataTableProps;
    availabilityByWarehouseTableHead?: DataTableHeadProps;
    availabilityByWarehouseTableHeader?: DataTableHeaderProps;
    availabilityByWarehouseTableBody?: DataTableBodyProps;
    availabilityByWarehouseTableRow?: DataTableRowProps;
    availabilityByWarehouseTableCell?: DataTableCellBaseProps;
    // XNG-CHANGE
    totalAvailabilityWrapper?: InjectableCss;
    availabilityWrapper?: InjectableCss;
    totalAvailableSmallHeading?: SmallHeadingAndTextStyles;
    inBranchAvailabilityWrapper?: InjectableCss;
    pdpMessageText?: TypographyPresentationProps;
    availabilityMessageText?: TypographyPresentationProps;
    inventoryOutOfStockPickupWrapper?: InjectableCss;
    notAvailabilityInWarehouseTableCell?: DataTableCellBaseProps;
}

export const productAvailabilityStyles: ProductAvailabilityStyles = {
    container: {
        gap: 0,
        css: css`
            flex-grow: 0;
        `,
    },
    messageGridItem: { width: 12 },
    availabilityByWarehouseLinkGridItem: { width: 12 },
    availabilityByWarehouseModal: {
        size: 400,
        cssOverrides: {
            modalContent: css`
                padding: 0;
            `,
            modalTitle: css`
                padding: 10px 10px 10px 20px;
            `,
            headlineTypography: css`
                font-size: 20px;
                margin-bottom: 0;
            `,
        },
    },
    centeringWrapper: {
        css: css`
            height: 100px;
            width: 100%;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: 0 auto;
        `,
    },
    messageText: {
        css: css`
            width: 100%;
            ${wrapInContainerStyles}
        `,
    },
    realTimeText: {
        weight: "bold",
        css: css`
            display: inline-block;
            text-align: left;
            min-width: 25px;

            &::after {
                overflow: hidden;
                display: inline-block;
                vertical-align: bottom;
                animation: ellipsis steps(4, end) 900ms infinite 1s;
                content: "\\2026";
                width: 0;
            }

            @keyframes ellipsis {
                to {
                    width: 1.25em;
                }
            }
        `,
    },
    errorText: {
        weight: "bold",
    },
};

const InventoryMessage = styled.div<{ color: string }>`
    display: flex;
    align-items: center;
    padding: 4px 8px;
    border-radius: 5px;
    background-color: ${props => safeColor(resolveColor(props.color, props.theme)).rgb().alpha(0.2).string()};
    ${IconWrapper} {
        margin-right: 8px;
    }
`;

const getAvailabilityColor = (
    productSettings: ProductSettingsModel,
    availability?: AvailabilityDto,
    product?: ProductModel,
    canBackOrder?: boolean,
) => {
    if (!productSettings.showInventoryAvailability) {
        return "common";
    }

    const availabilityType = availability?.messageType;
    if (availabilityType === AvailabilityMessageType.OutOfStock) {
        if (product?.detail?.canBackOrder || canBackOrder) {
            return "success";
        }
        return "danger";
    }

    if (availabilityType === AvailabilityMessageType.LowStock) {
        return "warning";
    }

    return "success";
};

const getAvailabilityIcon = (
    productSettings: ProductSettingsModel,
    availability?: AvailabilityDto,
    product?: ProductModel,
    canBackOrder?: boolean,
    useInNotify?: boolean,
) => {
    if (!productSettings.showInventoryAvailability) {
        return undefined;
    }

    const availabilityType = availability?.messageType;
    if (availabilityType === AvailabilityMessageType.OutOfStock) {
        if (product?.detail?.canBackOrder || canBackOrder) {
            return useInNotify ? InStock : AvailableForOrder;
        }
        return OutOfStock;
    }

    if (availabilityType === AvailabilityMessageType.Available) {
        return InStock;
    }

    return undefined;
};

const showLink = (
    availability: AvailabilityDto | undefined,
    trackInventory: boolean,
    productSettings: ProductSettingsModel,
    isProductDetailsPage?: boolean,
): boolean => {
    if (
        !availability ||
        availability.messageType === AvailabilityMessageType.NoMessage ||
        !trackInventory ||
        !productSettings.showInventoryAvailability ||
        !productSettings.displayInventoryPerWarehouse
    ) {
        return false;
    }

    if (!productSettings.displayInventoryPerWarehouseOnlyOnProductDetail) {
        return true;
    }

    return !!isProductDetailsPage;
};

const ProductAvailability = ({
    productId,
    availability,
    unitOfMeasure,
    trackInventory,
    productSettings,
    isProductDetailsPage,
    getRealTimeWarehouseInventory,
    failedToLoadInventory,
    showInventory,
    extendedStyles,
    // XNG-CHANGES
    showOutOfStockText,
    fulfillmentMethod,
    productInfo,
    product,
    isCartPage,
    totalAvailableQty,
    inBranchQty,
    canBackOrder,
    customAvailabilityMessage,
    hideWebInventory,
    // Xng-Changes
    xGoogleAnalytics,
    session,
}: Props) => {
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [warehouses, setWarehouses] = useState<WarehouseDto[] | undefined>(undefined);
    const [errorMessage, setErrorMessage] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const modalCloseHandler = () => {
        setModalIsOpen(false);
    };
    const viewAvailabilityByWarehouseClickHandler = () => {
        // XNG-Changes
        if (xGoogleAnalytics?.pdpWarehouseInventoryEvent) {
            const dataLayer = getDataLayer();
            if (dataLayer && product) {
                const clickText = translate("Availability by Warehouse");

                const productPricing = productInfo?.pricing;
                const unitNetPrice = productPricing?.unitNetPrice || 0;

                const items = [
                    {
                        item_id: product.productNumber || "",
                        item_name: product.productTitle || "",
                        affiliation: xGoogleAnalytics.affiliation || "",
                        discount: "",
                        index: 0,
                        item_brand: xGoogleAnalytics.includeProductBrand ? product.brand?.name || "" : undefined,
                        item_list_id: xGoogleAnalytics.includeProductCategory
                            ? product.properties?.["categoriesIds"] || ""
                            : undefined,
                        location_id: session.pickUpWarehouse?.id,
                        price: unitNetPrice,
                        google_business_vertical: "retail",
                        quantity: 1,
                        ...parseProductCategories(
                            product.properties?.itemCategoriesShortDescription,
                            true,
                            xGoogleAnalytics.includeProductCategory,
                        ),
                    },
                ];

                const googleAnalyticsData = {
                    event: xGoogleAnalytics.pdpWarehouseInventoryEventName,
                    click_text: clickText,
                    affiliation: xGoogleAnalytics.affiliation || "",
                    sales_rep: xGoogleAnalytics.salesRep || "",
                    is_rep: xGoogleAnalytics.isRep || "",
                    bill_to_num: xGoogleAnalytics.billToNum || "",
                    ship_to_num: xGoogleAnalytics.shipToNum || "",
                    cust_type: xGoogleAnalytics.customerType || "",
                    login_type: xGoogleAnalytics.loginType || "",
                    warehouse: xGoogleAnalytics.warehouse || "",
                    items,
                };

                dataLayer.push({ ecommerce: null });
                dataLayer.push(googleAnalyticsData);
            }
        }

        setIsLoading(true);
        setModalIsOpen(true);
        getRealTimeWarehouseInventory({
            productId,
            unitOfMeasure,
            expand: ["warehouses"],
            onComplete: result => {
                // XNG-Change: sort on description then save the value
                const resultWarehouses = result.warehouses;
                resultWarehouses?.sort((a, b) => {
                    const valA = a.description || a.name;
                    const valB = b.description || b.name;

                    return valA.localeCompare(valB);
                });
                setWarehouses(resultWarehouses);
                setErrorMessage(result.errorMessage);
                setIsLoading(false);
            },
        });
    };

    const color = getAvailabilityColor(productSettings, availability, product, canBackOrder);
    const iconSrc = getAvailabilityIcon(productSettings, availability, product, canBackOrder);
    const qtyInBranch = Number(productInfo?.inventory?.additionalResults?.qtyInBranch || 0);
    const qtyOnHand = productInfo?.inventory?.qtyOnHand || 0;
    const [styles] = useState(() => mergeToNew(productAvailabilityStyles, extendedStyles));
    if (!showInventory) {
        return null;
    }

    if (failedToLoadInventory) {
        return (
            <StyledWrapper {...styles.errorWrapper}>
                <Typography {...styles.errorText} data-test-selector="inventoryLoadFailedMessage">
                    {siteMessage("RealTimeInventory_InventoryLoadFailed")}
                </Typography>
            </StyledWrapper>
        );
    }

    let inventoryTextComponent = <Typography {...styles.realTimeText} />;
    // XNG-CHANGE
    if (
        fulfillmentMethod === FulfillmentMethod.Ship &&
        ((availability?.messageType === AvailabilityMessageType.OutOfStock &&
            availability?.message?.length &&
            showOutOfStockText) ||
            (canBackOrder && qtyInBranch <= 0))
    ) {
        if (canBackOrder && qtyInBranch <= 0) {
            if (!customAvailabilityMessage) {
                inventoryTextComponent = <></>;
            } else {
                inventoryTextComponent = (
                    <InventoryMessage color={color} {...styles.inventoryMessage}>
                        <Typography {...styles.availabilityMessageText} data-test-selector="availabilityMessage">
                            {customAvailabilityMessage}
                        </Typography>
                    </InventoryMessage>
                );
            }
        } else {
            inventoryTextComponent = (
                <InventoryMessage color={color} {...styles.inventoryMessage}>
                    <Icon
                        color={color}
                        src={getAvailabilityIcon(productSettings, availability, product, canBackOrder, true)}
                        size={14}
                    />
                    <Typography {...styles.availabilityMessageText} data-test-selector="availabilityMessage">
                        {availability?.message}
                    </Typography>
                </InventoryMessage>
            );
        }
    } else if (availability) {
        inventoryTextComponent = <></>;
    }

    // XNG-Changes
    return (
        <GridContainer {...styles.container}>
            <GridItem {...styles.messageGridItem}>
                {isProductDetailsPage ? (
                    <>
                        {fulfillmentMethod === FulfillmentMethod.Ship ? (
                            <StyledWrapper {...styles.totalAvailabilityWrapper}>
                                <Icon src={iconSrc} size={12} />
                                <Typography {...styles.pdpMessageText} data-test-selector="availabilityMessage">
                                    {availability?.message}
                                </Typography>
                            </StyledWrapper>
                        ) : (
                            <StyledWrapper {...styles.totalAvailabilityWrapper}>
                                <Icon src={qtyInBranch > 0 ? InStock : OutOfStock} size={12} />
                                <Typography {...styles.pdpMessageText} data-test-selector="availabilityMessage">
                                    {availability?.message}
                                </Typography>
                            </StyledWrapper>
                        )}
                    </>
                ) : isCartPage ? (
                    <>
                        {fulfillmentMethod === FulfillmentMethod.Ship ? (
                            <>
                                <StyledWrapper {...styles.availabilityWrapper}>
                                    {/* XNG-Chsnges: SC-1080 */}
                                    {!hideWebInventory && (
                                        <StyledWrapper {...styles.totalAvailabilityWrapper}>
                                            <Icon src={iconSrc} size={12} />
                                            <SmallHeadingAndText
                                                heading={siteMessage("Custom_TotalAvailability").toString()}
                                                text={
                                                    totalAvailableQty && totalAvailableQty > 0
                                                        ? totalAvailableQty
                                                        : availability?.message || ""
                                                }
                                                extendedStyles={styles.totalAvailableSmallHeading}
                                            />
                                        </StyledWrapper>
                                    )}
                                    <StyledWrapper {...styles.inBranchAvailabilityWrapper}>
                                        <Icon
                                            src={
                                                inBranchQty && inBranchQty > 0
                                                    ? InStock
                                                    : product?.detail?.canBackOrder
                                                      ? AvailableForOrder
                                                      : OutOfStock
                                            }
                                            size={12}
                                        />
                                        <SmallHeadingAndText
                                            heading={translate("In Branch")}
                                            text={
                                                inBranchQty && inBranchQty > 0
                                                    ? inBranchQty && inBranchQty
                                                    : product?.detail?.canBackOrder
                                                      ? siteMessage("Custom_AvailableForOrder").toString()
                                                      : siteMessage("Custom_InventoryOutOfStock").toString()
                                            }
                                            extendedStyles={styles.totalAvailableSmallHeading}
                                        />
                                    </StyledWrapper>
                                </StyledWrapper>
                            </>
                        ) : (
                            <StyledWrapper {...styles.inBranchAvailabilityWrapper}>
                                <Icon
                                    src={
                                        availability?.messageType === AvailabilityMessageType.OutOfStock
                                            ? OutOfStock
                                            : InStock
                                    }
                                    size={12}
                                />
                                <Typography {...styles.messageText} data-test-selector="availabilityMessage">
                                    {availability?.message}
                                </Typography>
                            </StyledWrapper>
                        )}
                    </>
                ) : (
                    <>
                        {fulfillmentMethod === FulfillmentMethod.Ship ? (
                            <StyledWrapper {...styles.availabilityWrapper}>
                                {/* XNG-Changes: SC-1080 */}
                                {!hideWebInventory && (
                                    <>
                                        <StyledWrapper {...styles.totalAvailabilityWrapper}>
                                            <Icon src={iconSrc} size={12} />
                                            <SmallHeadingAndText
                                                heading={siteMessage("Custom_TotalAvailability").toString()}
                                                text={qtyOnHand > 0 ? qtyOnHand : availability?.message || ""}
                                                extendedStyles={styles.totalAvailableSmallHeading}
                                            />
                                        </StyledWrapper>
                                    </>
                                )}
                                <StyledWrapper {...styles.inBranchAvailabilityWrapper}>
                                    <Icon
                                        src={
                                            qtyInBranch > 0
                                                ? InStock
                                                : product?.detail?.canBackOrder
                                                  ? AvailableForOrder
                                                  : OutOfStock
                                        }
                                        size={12}
                                    />
                                    <SmallHeadingAndText
                                        heading={translate("In Branch")}
                                        text={
                                            qtyInBranch > 0
                                                ? qtyInBranch
                                                : product?.detail?.canBackOrder
                                                  ? siteMessage("Custom_AvailableForOrder").toString()
                                                  : siteMessage("Custom_InventoryOutOfStock").toString()
                                        }
                                        extendedStyles={styles.totalAvailableSmallHeading}
                                    />
                                </StyledWrapper>
                            </StyledWrapper>
                        ) : (
                            <>
                                {qtyInBranch > 0 ? (
                                    <StyledWrapper {...styles.inBranchAvailabilityWrapper}>
                                        <Icon src={InStock} size={12} />
                                        <SmallHeadingAndText
                                            heading={translate("In Branch")}
                                            text={qtyInBranch}
                                            extendedStyles={styles.totalAvailableSmallHeading}
                                        />
                                    </StyledWrapper>
                                ) : (
                                    <StyledWrapper {...styles.inventoryOutOfStockPickupWrapper}>
                                        <Icon src={OutOfStock} size={12} />
                                        <Typography className="inventory-out-of-stock-pickup">
                                            {siteMessage("Custom_InventoryOutOfStock").toString()}
                                        </Typography>
                                    </StyledWrapper>
                                )}
                            </>
                        )}
                    </>
                )}
            </GridItem>
            <GridItem {...styles.messageGridItem}>{inventoryTextComponent}</GridItem>
            {/* XNG-Changes: SC-1080 */}
            {showLink(availability, trackInventory, productSettings, isProductDetailsPage) && !hideWebInventory && (
                <GridItem {...styles.availabilityByWarehouseLinkGridItem}>
                    <Link
                        {...styles.availabilityByWarehouseLink}
                        onClick={viewAvailabilityByWarehouseClickHandler}
                        data-test-selector="availabilityByWarehouseLink"
                    >
                        {translate("Availability by Warehouse")}
                    </Link>
                    <Modal
                        {...styles.availabilityByWarehouseModal}
                        headline={translate("Warehouse Inventory")}
                        isOpen={modalIsOpen}
                        handleClose={modalCloseHandler}
                    >
                        {isLoading && (
                            <StyledWrapper {...styles.centeringWrapper}>
                                <LoadingSpinner {...styles.spinner} />
                            </StyledWrapper>
                        )}
                        {!isLoading && (
                            <>
                                {errorMessage ? (
                                    <div>{errorMessage}</div>
                                ) : (
                                    <DataTable
                                        {...styles.availabilityByWarehouseTable}
                                        data-test-selector="availabilityByWarehouseModal"
                                    >
                                        <DataTableHead {...styles.availabilityByWarehouseTableHead}>
                                            <DataTableHeader {...styles.availabilityByWarehouseTableHeader}>
                                                {translate("Warehouse")}
                                            </DataTableHeader>
                                            <DataTableHeader {...styles.availabilityByWarehouseTableHeader}>
                                                {translate("QTY")}
                                            </DataTableHeader>
                                        </DataTableHead>
                                        <DataTableBody {...styles.availabilityByWarehouseTableBody}>
                                            {warehouses?.map(warehouse => (
                                                <DataTableRow
                                                    {...styles.availabilityByWarehouseTableRow}
                                                    key={warehouse.name}
                                                >
                                                    <DataTableCell
                                                        {...styles.availabilityByWarehouseTableCell}
                                                        data-test-selector="warehouseAvailabilityModal_Description"
                                                    >
                                                        {warehouse.description} ({warehouse.name})
                                                    </DataTableCell>
                                                    <DataTableCell
                                                        {...styles.availabilityByWarehouseTableCell}
                                                        data-test-selector="warehouseAvailabilityModal_Quantity"
                                                    >
                                                        {warehouse.qty}
                                                    </DataTableCell>
                                                </DataTableRow>
                                            ))}
                                            {!warehouses?.length && (
                                                <DataTableRow>
                                                    <DataTableCell {...styles.notAvailabilityInWarehouseTableCell}>
                                                        <Notify type="info" showClose={false}>
                                                            {siteMessage("Custom_NOT_AVAILABLE_IN_WAREHOUSES")}
                                                        </Notify>
                                                    </DataTableCell>
                                                </DataTableRow>
                                            )}
                                        </DataTableBody>
                                    </DataTable>
                                )}
                            </>
                        )}
                    </Modal>
                </GridItem>
            )}
        </GridContainer>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductAvailability);
